/**
 * PageHeader — the title block at the top of every page (per ui-design.md §4.4).
 *
 * Structure: title (h1) + optional subtitle + optional right-aligned action slot.
 * Use this primitive at the top of every route under AppShell so the layout
 * grammar stays consistent without per-page handcrafted headers.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './PageHeader.module.css';

export interface PageHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: ReactNode;
  /** Primary action(s) rendered right-aligned. Usually a <Button>. */
  action?: ReactNode;
  /**
   * Small inline element rendered immediately after the title — typically
   * an InfoHint or other lightweight badge. Sits to the right of the title
   * within the title row, NOT in the action slot.
   */
  titleAdornment?: ReactNode;
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(function PageHeader(
  { title, subtitle, action, titleAdornment, className, ...rest },
  ref,
) {
  const composedClassName = [styles.root, className ?? ''].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={composedClassName} {...rest}>
      <div className={styles.text}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
          {titleAdornment}
        </div>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>
      {action ? <div className={styles.actions}>{action}</div> : null}
    </div>
  );
});
